import { useState, useRef, RefObject } from "react";
import {
  Button,
  Modal,
  DateInput,
  TextAreaInput,
  Grid,
} from "@monolith-forensics/monolith-ui";
import { useForm } from "@mantine/form";
import { z } from "zod";
import validateAsync from "@/utils/ZodHelpers/validateAsync";
import ButtonMenu from "@/components/Forms/components/ButtonMenu.js";
import UserCocSelector from "@/components/Forms/components/Selectors/UserCocSelector";
import { usePermissions } from "@/hooks/usePermissions";
import { useSnackbar } from "notistack";
import { getIpAddress } from "@/api";
import { db_timestamp } from "@/utils/date-format";

import type {
  BaseCocModalProps,
  UserOption,
  CocSignatureObject,
} from "../Coc.types";
import EvidenceCocAPI from "@/api/evidence/coc";
import { createSignature } from "../utils";
import { SignatureField, SignatureFieldRef } from "@/Features/Signatures";

interface DestroyCocModalProps extends BaseCocModalProps {}

interface DestroyFormData {
  destroyed_by: UserOption | null;
  timestamp: string;
  reason: string;
}

const destroyValidationSchema = z.object({
  destroyed_by: z
    .object(
      {
        user_id: z.number(),
        full_name: z.string(),
      },
      {
        required_error: "Destroyed by user is required",
      }
    )
    .refine((val) => val !== null && val !== undefined, {
      message: "Destroyed by user is required",
    }),
  timestamp: z
    .string({
      required_error: "Timestamp is required",
    })
    .min(1, { message: "Timestamp is required" }),
  reason: z.string().optional(),
});

const DestroyCocModal: React.FC<DestroyCocModalProps> = ({
  show,
  onClose,
  onSubmit,
  defaultInfo,
}) => {
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<DestroyFormData>({
    mode: "uncontrolled",
    initialValues: {
      destroyed_by: null,
      timestamp: new Date().toISOString(),
      reason: "",
    },
  });

  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();

  const destroyedBySig = useRef<SignatureFieldRef>();

  const handleSubmit = async () => {
    const formData = form.getValues();

    const validationResult = await validateAsync(destroyValidationSchema)(
      formData
    );
    if (validationResult?.hasErrors) {
      console.error("Validation Errors", validationResult?.errors);
      form.setErrors(validationResult?.errors);
      return;
    }

    setSubmitting(true);
    try {
      const ipAddress = await getIpAddress();

      const signature: CocSignatureObject = {
        custody_from_sig: createSignature(
          destroyedBySig as RefObject<SignatureFieldRef>,
          ipAddress,
          currentUser
        ),
      };

      const requestObj = {
        caseInfo: {
          case_id: defaultInfo.case_id,
          case_number: defaultInfo.case_number,
          case_ref: defaultInfo.case_ref,
        },
        evidenceInfo: {
          evidence_id: defaultInfo.evidence_id,
          uuid: defaultInfo.uuid,
          evidence_number: defaultInfo.evidence_number,
        },
        moveData: {
          entered_by_id: currentUser.user_id,
          entered_by: currentUser.full_name,
          new_location_id: -1,
          new_location_name: "Destroyed",
          current_location_name: formData.destroyed_by?.full_name || "",
          reason: formData.reason || "Evidence Destroyed",
          timestamp: db_timestamp(formData.timestamp, true),
          signature: JSON.stringify(signature),
          coc_type: "Destroy",
        },
      };

      await EvidenceCocAPI.moveEvidence(requestObj);

      enqueueSnackbar(`Evidence Item Destroyed`, {
        variant: "success",
      });

      onSubmit?.(requestObj);
    } catch (error) {
      console.error("Error destroying evidence:", error);
      enqueueSnackbar(`Error destroying evidence item.`, {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
      onClose?.();
    }
  };

  const handleCancel = () => {
    form.reset();
    onClose?.();
  };

  return (
    <Modal open={show} onClose={onClose} style={{ width: 600 }}>
      <Modal.Title>Destroy Evidence Item</Modal.Title>
      <div style={{ margin: "20px 0px" }}>
        <Grid col={1}>
          <UserCocSelector
            label="Destroyed By"
            required
            placeholder="Select the user destroying this evidence..."
            enabled={show}
            key={form.key("destroyed_by")}
            {...form.getInputProps("destroyed_by")}
          />
          <DateInput
            label="Timestamp"
            required
            format="YYYY-MM-DD HH:mm:ss"
            enableCalendar
            arrow
            key={form.key("timestamp")}
            {...form.getInputProps("timestamp")}
          />
          <TextAreaInput
            label="Destroy Notes"
            minRows={3}
            key={form.key("reason")}
            {...form.getInputProps("reason")}
          />
          <div>
            <SignatureField
              sigFieldRef={destroyedBySig}
              label="Destroyed By Signature"
              useESignature={true}
            />
          </div>
        </Grid>
      </div>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          size="xs"
          color="error"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        >
          Destroy Evidence
        </Button>
      </ButtonMenu>
    </Modal>
  );
};

export default DestroyCocModal;
